/**
 * MobilePostOverlay — Fullscreen overlay for post creation on mobile.
 * Renders via React Portal so it breaks out of SubPanelSwiper transforms.
 * Same UI pattern as MobileThemeBottomSheet (FullViewportOverlay).
 *
 * Supports optional community features:
 *  - Community selector dropdown
 *  - Post type toggle (Regular / Forum)
 *  - Visibility toggle (Public / Members Only)
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaUpload, getMediaUrl } from '../../hooks/useMediaUpload';
import resolveAvatar from '../../lib/resolveAvatar';

interface MobilePostOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (postData: {
    title: string;
    content: string;
    tags: string[];
    mediaUrl: string;
    mediaPosition: 'above' | 'below';
    communityId?: string;
    postType?: 'community' | 'forum';
    visibility?: 'public' | 'members-only';
  }) => Promise<void>;
  currentUser: {
    id: string;
    username: string;
    displayName?: string;
    avatar?: string;
  } | null;
  /** Optional heading override, defaults to "Create Post" */
  heading?: string;
  /** List of communities for the selector dropdown */
  communities?: { id: string; name: string }[];
  /** Pre-selected community ID */
  selectedCommunityId?: string;
  /** Hide the community selector (e.g. when posting from a specific community page) */
  hideCommunitySelector?: boolean;
  /** Show the Regular Post / Forum Post toggle */
  showPostTypeSelector?: boolean;
  /** Show the Public / Members Only visibility toggle */
  showVisibilitySelector?: boolean;
}

export function MobilePostOverlay({
  isOpen,
  onClose,
  onSubmit,
  currentUser,
  heading = 'Create Post',
  communities,
  selectedCommunityId,
  hideCommunitySelector = false,
  showPostTypeSelector = false,
  showVisibilitySelector = false,
}: MobilePostOverlayProps) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [mediaUrl, setMediaUrl] = useState('');
  const [mediaPosition, setMediaPosition] = useState<'above' | 'below'>('below');
  const [communityId, setCommunityId] = useState(selectedCommunityId || '');
  const [postType, setPostType] = useState<'community' | 'forum'>('community');
  const [visibility, setVisibility] = useState<'public' | 'members-only'>('public');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile, isUploading, uploadProgress, fileId, error: uploadError } = useMediaUpload();

  // Sync selectedCommunityId prop
  useEffect(() => {
    if (selectedCommunityId) setCommunityId(selectedCommunityId);
  }, [selectedCommunityId]);

  // Update media URL when file upload completes
  useEffect(() => {
    if (fileId) setMediaUrl(getMediaUrl(fileId));
  }, [fileId]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  const isCommunityMode = !!(communities && communities.length > 0);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    // If community mode with selector, require a community selection
    if (isCommunityMode && !hideCommunitySelector && !communityId) return;
    setIsSubmitting(true);
    try {
      const tags = tagInput.split(/[,\s#]+/).map(t => t.trim()).filter(t => t.length > 0);
      await onSubmit({
        title,
        content,
        tags,
        mediaUrl,
        mediaPosition,
        ...(isCommunityMode ? { communityId, postType, visibility } : {}),
      });
      setTitle('');
      setContent('');
      setTagInput('');
      setMediaUrl('');
      setMediaPosition('below');
      setPostType('community');
      setVisibility('public');
      if (!selectedCommunityId) setCommunityId('');
      onClose();
    } catch (error) {
      console.error('Failed to create post:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try { await uploadFile(file); } catch { /* silent */ }
    }
    if (e.target) e.target.value = '';
  };

  const accentColor = 'var(--color-accent, #06b6d4)';
  const textColor = 'var(--text-color, #fff)';
  const inputBg = 'rgba(255, 255, 255, 0.06)';
  const inputBorder = '1px solid rgba(255,255,255,0.1)';

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 30 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="fixed inset-0 z-[101] flex flex-col"
          style={{ background: 'var(--app-bg, #0a0a0a)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="shrink-0 flex items-center justify-between px-5"
            style={{
              background: 'var(--navbar-bg, rgba(10,10,26,0.95))',
              borderBottom: '1px solid rgba(255,255,255,0.08)',
              paddingTop: 'max(env(safe-area-inset-top, 0px), 12px)',
              paddingBottom: 12,
            }}
          >
            <h2 className="text-lg font-bold" style={{ color: textColor }}>
              {heading}
            </h2>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)' }}
            >
              <X size={20} style={{ color: textColor }} />
            </button>
          </div>

          {/* Scrollable form content */}
          <div
            className="flex-1 overflow-y-auto px-5 pb-8 pt-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* User Info */}
            {currentUser && (
              <div className="flex items-center gap-3 mb-5">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${accentColor}, ${accentColor})` }}
                >
                  {currentUser.avatar ? (
                    <img
                      src={resolveAvatar(currentUser.avatar) || currentUser.avatar}
                      alt="You"
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <span style={{ color: textColor }}>
                      {(currentUser.displayName || currentUser.username).charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-semibold text-sm" style={{ color: textColor }}>
                    {currentUser.displayName || currentUser.username}
                  </p>
                  <p className="text-xs opacity-70" style={{ color: textColor }}>
                    @{currentUser.username}
                  </p>
                </div>
              </div>
            )}

            {/* Community Selector */}
            {isCommunityMode && !hideCommunitySelector && (
              <div className="mb-4">
                <select
                  value={communityId}
                  onChange={(e) => setCommunityId(e.target.value)}
                  className="w-full p-3 rounded-xl focus:outline-none"
                  style={{
                    backgroundColor: inputBg,
                    color: textColor,
                    border: inputBorder,
                    fontSize: 16,
                  }}
                >
                  <option value="" style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                    Select a community...
                  </option>
                  {communities!.map((c) => (
                    <option key={c.id} value={c.id} style={{ backgroundColor: '#1a1a2e', color: '#fff' }}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Post Type Selector */}
            {showPostTypeSelector && (
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setPostType('community')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: postType === 'community' ? accentColor : inputBg,
                    color: postType === 'community' ? '#fff' : textColor,
                    border: `1px solid ${postType === 'community' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Regular Post
                </button>
                <button
                  onClick={() => setPostType('forum')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: postType === 'forum' ? accentColor : inputBg,
                    color: postType === 'forum' ? '#fff' : textColor,
                    border: `1px solid ${postType === 'forum' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Forum Post
                </button>
              </div>
            )}

            {/* Title Input */}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Title (optional)..."
              className="w-full px-4 py-3 rounded-xl text-base font-bold mb-3 focus:outline-none"
              style={{ backgroundColor: inputBg, border: inputBorder, color: textColor }}
              maxLength={100}
            />

            {/* Content Textarea */}
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={postType === 'forum' ? 'Start a discussion...' : 'Share your Earth-positive actions...'}
              className="w-full rounded-xl p-4 resize-none focus:outline-none"
              style={{ backgroundColor: inputBg, border: inputBorder, color: textColor, minHeight: 160, fontSize: 16 }}
              maxLength={5000}
            />
            <div className="text-right text-xs mt-1 opacity-50" style={{ color: textColor }}>
              {content.length}/5000
            </div>

            {/* Media Section */}
            <div className="mt-4 mb-4">
              <input
                type="text"
                value={mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                placeholder="Image URL (optional)"
                className="w-full p-3 rounded-xl mb-3 focus:outline-none"
                style={{ backgroundColor: inputBg, border: inputBorder, color: textColor, fontSize: 16 }}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-50"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    color: textColor,
                    border: inputBorder,
                  }}
                >
                  <Upload size={16} />
                  {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Media'}
                </button>
                {mediaUrl && (
                  <button
                    onClick={() => setMediaUrl('')}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium"
                    style={{
                      backgroundColor: 'rgba(255, 0, 0, 0.15)',
                      color: '#ff6b6b',
                      border: '1px solid rgba(255, 0, 0, 0.3)',
                    }}
                  >
                    <X size={16} />
                    Clear
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={handleFileChange}
              />

              {/* Media preview */}
              {mediaUrl && (
                <div className="mt-3 rounded-xl overflow-hidden">
                  <img src={mediaUrl} alt="Preview" className="w-full h-auto max-h-48 object-cover" />
                </div>
              )}

              {/* Media Position */}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setMediaPosition('above')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: mediaPosition === 'above' ? accentColor : inputBg,
                    color: mediaPosition === 'above' ? '#fff' : textColor,
                    border: `1px solid ${mediaPosition === 'above' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Above Content
                </button>
                <button
                  onClick={() => setMediaPosition('below')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: mediaPosition === 'below' ? accentColor : inputBg,
                    color: mediaPosition === 'below' ? '#fff' : textColor,
                    border: `1px solid ${mediaPosition === 'below' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Below Content
                </button>
              </div>
            </div>

            {/* Tags Input */}
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="Tags (e.g. #tag1 #tag2 or tag1, tag2)"
              className="w-full p-3 rounded-xl mb-4 focus:outline-none"
              style={{ backgroundColor: inputBg, border: inputBorder, color: textColor, fontSize: 16 }}
            />

            {/* Visibility Selector */}
            {showVisibilitySelector && (
              <div className="flex gap-2 mb-5">
                <button
                  onClick={() => setVisibility('public')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: visibility === 'public' ? accentColor : inputBg,
                    color: visibility === 'public' ? '#fff' : textColor,
                    border: `1px solid ${visibility === 'public' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Public
                </button>
                <button
                  onClick={() => setVisibility('members-only')}
                  className="flex-1 px-3 py-2.5 rounded-xl text-sm font-medium"
                  style={{
                    backgroundColor: visibility === 'members-only' ? accentColor : inputBg,
                    color: visibility === 'members-only' ? '#fff' : textColor,
                    border: `1px solid ${visibility === 'members-only' ? accentColor : 'rgba(255,255,255,0.1)'}`,
                  }}
                >
                  Members Only
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={!content.trim() || isSubmitting || (isCommunityMode && !hideCommunitySelector && !communityId)}
              className="w-full py-3.5 rounded-xl font-semibold text-base disabled:opacity-50"
              style={{ backgroundColor: accentColor, color: '#fff' }}
            >
              {isSubmitting ? 'Posting...' : isCommunityMode ? 'Post to Community' : 'Post'}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
